/**
 * The trade list as a card (2026-09-29 owner ask): the Pokémon marked with
 * the round trade button on My Pokémon.
 *
 * Two homes, one component:
 *   - a friend's Potential Trades tab passes `onOffer`: "From your trade
 *     list", each row offerable to THIS friend with one tap. Offering
 *     earmarks it on the friend trade list (direction "give"), where it can
 *     go into a proposal like any other lead.
 *   - the Trade page passes nothing: "Your trade list", the same rows with a
 *     take-off control, and a pointer to a friend's tab to offer them from.
 *     Added 2026-09-29 (owner report: "Pokémon added to the trade list
 *     aren't showing up on the trade page"; the list only ever appeared on a
 *     friend's tab).
 *
 * Renders nothing while the list is empty.
 */
import { ArrowLeftRight, CheckCircle2, X } from "lucide-react";
import { useLocation } from "wouter";
import { CaughtSprite } from "@/components/CaughtSprite";
import { VariantBadge } from "@/components/dexVariantVisuals";
import type { PlannedTrade } from "@/components/trade/compareTypes";
import { ivPercent, useMyPokemonRecords, type MyPokemonRecord } from "@/lib/myPokemonStore";
import { myPokemonDisplayName } from "@/lib/myPokemonFilter";
import { recordArtCandidates } from "@/lib/recordArt";
import { recordCompareVariant } from "@/lib/recordCompareVariant";
import { removeFromTradeList, useTradeList } from "@/lib/tradeListStore";

export function TradeListOffersBlock({
  friendName,
  trades = [],
  onOffer,
  filter,
}: {
  /** The friend's name for the Offer button; absent on the Trade page. */
  friendName?: string;
  /** The friend trade list, to mark records already offered. */
  trades?: readonly PlannedTrade[];
  /** Present on a friend's tab. Absent, the row's control takes it off the list. */
  onOffer?: (record: MyPokemonRecord) => void;
  /** The tab's lead filters, applied to the listed records too (2026-09-29). */
  filter?: (record: MyPokemonRecord) => boolean;
}) {
  const [, setLocation] = useLocation();
  const ids = useTradeList();
  const records = useMyPokemonRecords();
  const all = ids.map((id) => records.find((r) => r.id === id)).filter((r): r is MyPokemonRecord => r != null);
  if (all.length === 0) return null;
  const listed = filter ? all.filter(filter) : all;
  const hidden = all.length - listed.length;
  const offered = new Set(
    trades.filter((t) => t.direction === "give" && t.status !== "hidden").map((t) => `${t.variant}:${t.speciesId}`),
  );
  const friendMode = !!onOffer;
  return (
    <section className={`v2-neon-card-soft p-4 ${friendMode ? "mt-5 mb-5" : ""}`} data-testid="trade-list-offers" data-mode={friendMode ? "friend" : "list"}>
      <h2
        className="text-[12px] font-black uppercase tracking-[0.14em] inline-flex items-center gap-1.5"
        style={{ color: "var(--text-ink-faint)" }}
      >
        <ArrowLeftRight className="w-3.5 h-3.5" style={{ color: "var(--accent-fill)" }} />
        {friendMode ? "From your trade list" : "Your trade list"}
      </h2>
      <p className="mt-1 text-[10.5px]" style={{ color: "var(--text-tertiary)" }}>
        {friendMode
          ? `Offer one to ${friendName} and it joins the trade list above, ready for a proposal.`
          : "Tap one to open it. To offer one, open a friend and their Potential Trades tab lists these."}
      </p>
      {hidden > 0 && (
        <p className="mt-2 text-[10.5px]" style={{ color: "var(--text-tertiary)" }} data-testid="trade-list-offers-hidden">
          {hidden} on your list {hidden === 1 ? "is" : "are"} hidden by the filters above.
        </p>
      )}
      <ul className="mt-3 space-y-2">
        {listed.map((r) => {
          const variant = recordCompareVariant(r);
          const done = r.speciesId != null && offered.has(`${variant}:${r.speciesId}`);
          const pct = ivPercent(r);
          return (
            <li key={r.id} className="flex items-center gap-2 rounded-2xl p-2 surface-elev" data-testid={`trade-list-offer-${r.id}`} data-offered={done ? "1" : undefined}>
              <button
                type="button"
                onClick={() => setLocation(`/my-pokemon/${r.id}`)}
                className="flex items-center gap-2 min-w-0 flex-1 text-left"
                aria-label={`Open ${myPokemonDisplayName(r)} in My Pokémon`}
              >
                <span className="w-10 h-10 shrink-0 grid place-items-center">
                  {r.speciesId != null && <CaughtSprite id={r.speciesId} size={40} shiny={!!r.isShiny} formSpriteCandidates={recordArtCandidates(r)} />}
                </span>
                <span className="min-w-0">
                  <span className="block text-[13px] font-bold truncate" style={{ color: "var(--text-ink-bright)" }}>
                    {myPokemonDisplayName(r)}
                  </span>
                  <span className="block text-[11px] tabular-nums whitespace-nowrap" style={{ color: "var(--text-secondary)" }}>
                    {r.cp != null ? `CP ${r.cp}` : "CP ?"}
                    {pct != null ? ` · ${Math.round(pct)}%` : ""}
                  </span>
                  {variant !== "pokemon" && (
                    <span className="mt-1 inline-flex">
                      <VariantBadge variant={variant} />
                    </span>
                  )}
                </span>
              </button>
              {!friendMode ? (
                <button
                  type="button"
                  onClick={() => removeFromTradeList(r.id)}
                  data-testid={`trade-list-offer-${r.id}-remove`}
                  aria-label={`Take ${myPokemonDisplayName(r)} off the trade list`}
                  className="shrink-0 w-8 h-8 rounded-full grid place-items-center"
                  style={{ background: "var(--surface-sunken)", border: "1px solid var(--border-subtle)", color: "var(--text-tertiary)" }}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              ) : (
              <button
                type="button"
                disabled={done || r.speciesId == null}
                onClick={() => onOffer(r)}
                data-testid={`trade-list-offer-${r.id}-add`}
                className="shrink-0 rounded-full px-3 py-1.5 text-[11px] font-bold inline-flex items-center gap-1"
                style={{
                  minHeight: 32,
                  background: done ? "var(--surface-sunken)" : "var(--accent-fill)",
                  color: done ? "var(--text-tertiary)" : "var(--text-on-accent)",
                  border: `1px solid ${done ? "var(--border-subtle)" : "var(--accent-fill)"}`,
                }}
              >
                {done ? (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5" aria-hidden /> Offered
                  </>
                ) : (
                  `Offer to ${friendName}`
                )}
              </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
